import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchCrmOpportunitiesById,
  updateCrmOpportunity
} from '../../../services/crmOpportunityService';
import { fetchCrmInstitutes } from '../../../services/crmInstituteService';
import { fetchUsers } from '../../../services/userService';

const priorities = ['High', 'Medium', 'Low'];
const statuses = ['Negotiation', 'Won', 'Lost'];

const EditOpportunity: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    crm_institute_id: '',
    assigned_to: '',
    priority: '',
    status: '',
    source: '',
    expected_close_date: ''
  });

  const [institutes, setInstitutes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch opportunity details
        const opportunityData = await fetchCrmOpportunitiesById(id);
        setFormData({
          crm_institute_id: opportunityData.crm_institute_id,
          assigned_to: opportunityData.assigned_to,
          priority: opportunityData.priority,
          status: opportunityData.status,
          source: opportunityData.source || '',
          expected_close_date: opportunityData.expected_close_date
            ? opportunityData.expected_close_date.split('T')[0]
            : ''
        });

        // Fetch Institutes & Users
        const instituteData = await fetchCrmInstitutes();
        const userData = await fetchUsers();
        setInstitutes(instituteData);
        setUsers(userData);
      } catch (err) {
        setError('Failed to load opportunity data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await updateCrmOpportunity(id, formData);
      setSuccessMessage(
        response.message || 'Opportunity updated successfully!'
      );
      setTimeout(() => navigate('/crm/opportunities/all-opportunities'), 2000);
    } catch (err) {
      setError('Failed to update opportunity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return <Spinner animation="border" className="d-block mx-auto" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="container mt-4">
      <Card className="shadow p-4">
        <Card.Body>
          <Card.Title className="fw-bold">Edit Opportunity</Card.Title>

          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Institute</Form.Label>
              <Form.Select
                value={formData.crm_institute_id}
                onChange={e =>
                  setFormData({ ...formData, crm_institute_id: e.target.value })
                }
                required
                disabled
              >
                <option value="">Select Institute</option>
                {institutes.map(inst => (
                  <option
                    key={inst.crm_institute_id}
                    value={inst.crm_institute_id}
                  >
                    {inst.institute_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Assigned To</Form.Label>
              <Form.Select
                value={formData.assigned_to}
                onChange={e =>
                  setFormData({ ...formData, assigned_to: e.target.value })
                }
                required
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.username}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={formData.priority}
                onChange={e =>
                  setFormData({ ...formData, priority: e.target.value })
                }
                required
              >
                <option value="">Select Priority</option>
                {priorities.map(priority => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={formData.status}
                onChange={e =>
                  setFormData({ ...formData, status: e.target.value })
                }
                required
              >
                <option value="">Select Status</option>
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Source</Form.Label>
              <Form.Control
                type="text"
                value={formData.source}
                onChange={e =>
                  setFormData({ ...formData, source: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expected Close Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.expected_close_date}
                onChange={e =>
                  setFormData({
                    ...formData,
                    expected_close_date: e.target.value
                  })
                }
              />
            </Form.Group>

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Opportunity'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditOpportunity;
